import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";
import debounce from 'lodash.debounce';

export default class HomeFeaturedCases  {
	constructor(elm) {

        this.elm = elm;
        this.tlEl = this.elm.querySelector('[data-home-featured-cases-tl]');
        this.pagination = this.elm.querySelector('[data-home-featured-cases-pagination]');
        this.mask = this.elm.querySelector('[data-home-featured-cases-mask]');
        this.cases = this.elm.querySelectorAll('[data-home-featured-cases-case]');
        this.lastCaseText = this.elm.querySelector('[data-last-case-text]');

        this.boundResizeListener = debounce(this.handleResize.bind(this), 100);
		window.addEventListener('resize', this.boundResizeListener);
		this.boundResizeListener();

        this.init();
	}

    defineMasks() {
        const headerHeight = document.querySelector('[data-header]').getBoundingClientRect().height;
        this.initialMaskMobile = 'inset('+headerHeight+'px '+(((window.innerWidth/100)*5)+5)+'px '+(((window.innerWidth/100)*5)+5)+'px '+(((window.innerWidth/100)*5)+5)+'px round 5px)';
        this.initialMaskDesktop = 'inset('+headerHeight+'px '+(((window.innerWidth/100)*2.5)+10)+'px '+(((window.innerWidth/100)*2.5)+10)+'px '+(((window.innerWidth/100)*2.5)+10)+'px round 10px)';
        this.fullMask = 'inset(-10px -10px -10px -10px round 10px)';
        this.gridMaskMobile = 'inset('+Math.round((((window.innerHeight-(((window.innerWidth/20)*18)*1.2)+5)-this.lastCaseText.scrollHeight)))+'px  '+Math.round((((window.innerWidth/100)*5)+5))+'px '+Math.round((this.lastCaseText.scrollHeight+5))+'px '+Math.round((((window.innerWidth/100)*5)+5))+'px round 5px)';
        this.gridMaskDesktop = 'inset('+Math.round((((window.innerHeight-((window.innerWidth/20)*8))+10)-(this.lastCaseText.scrollHeight)))+'px '+Math.round((((window.innerWidth/100)*10)+10))+'px '+Math.round((this.lastCaseText.scrollHeight+10))+'px '+Math.round((((window.innerWidth/100)*10)+10))+'px round 10px)';
    }

    handleResize() {
        this.defineMasks();
    }

	init() {
        this.defineMasks();

        this.index = null;
        this.activeSlideIndex = 0;
        this.activeSlide = this.cases[this.activeSlideIndex];
        this.slideCount = this.cases.length;

        let mm = gsap.matchMedia();

        mm.add({
            isMobile: `(max-width: 575px)`,
            isDesktop: `(min-width: 576px)`,
            reduceMotion: "(prefers-reduced-motion: reduce)",
            hasHover: "(hover: hover) and (pointer: fine)"
          }, (context) => {

            let { isDesktop, isMobile, reduceMotion, hasHover } = context.conditions;

            this.currentMaskPath = isDesktop ? this.initialMaskDesktop : this.initialMaskMobile;
            gsap.set(this.mask, {
                clipPath: this.currentMaskPath,
            });

            gsap.set(this.cases[0], {
                scale: 0.95
            });
            this.maskState = 'initial';
            this.elm.setAttribute('data-mask', this.maskState);

            this.changeMask = (size) => {
                if(this.animatingMask) {
                    return;
                }
                if(size===this.maskState) {
                    return;
                }

                this.maskState = size;
                this.elm.setAttribute('data-mask', this.maskState);
                if(this.maskState==='full') {
                    document.body.classList.add('body--header-inverted');
                } else {
                    document.body.classList.remove('body--header-inverted');
                }
                window.dispatchEvent(new CustomEvent('disable-smooth-scroll'));

                let maskDesktop = null;
                let maskMobile = null;
                if(this.maskState==='initial') {
                    window.dispatchEvent(new CustomEvent('enable-smooth-scroll'));
                    maskDesktop = this.initialMaskDesktop;
                    maskMobile = this.initialMaskMobile;

                    gsap.to(this.cases[0], {
                        scale: 0.95,
                        ease: 'power2.inOut',
                        duration: 0.8
                    });

                    this.cases.forEach(elm => {
                        this.hideSlideText(elm);
                    });
                }
                if(this.maskState==='full') {
                    maskDesktop = this.fullMask;
                    maskMobile = this.fullMask;

                    this.showSlideText(this.activeSlide);

                    this.elm.classList.add('m-home-featured-cases--timeline');

                    if(this.activeSlideIndex===0) {
                        gsap.to(this.cases[0], {
                            scale: 1,
                            ease: 'power2.inOut',
                            duration: 0.6
                        });
                    } else {
                        gsap.fromTo(this.cases[this.cases.length-1], {
                            scale: 0.9,
                        }, {
                            scale: 1,
                            ease: 'power2.inOut',
                            duration: 0.6
                        });
                    }
                }
                if(this.maskState==='grid') {
                    window.dispatchEvent(new CustomEvent('enable-smooth-scroll'));

                    maskDesktop = this.gridMaskDesktop;
                    maskMobile = this.gridMaskMobile;

                    this.cases.forEach(elm => {
                        this.hideSlideText(elm);
                    });

                    this.elm.classList.remove('m-home-featured-cases--timeline');

                    gsap.fromTo(this.cases[this.cases.length-1], {
                        scale: 1,
                    }, {
                        scale: 0.9,
                        ease: 'power2.inOut',
                        duration: 1.2
                    });
                }

                gsap.fromTo(this.mask, {
                    clipPath: this.currentMaskPath,
                }, {
                    clipPath: isDesktop ? maskDesktop : maskMobile,
                    ease: 'power2.inOut',
                    duration: 0.8,
                    onStart: () => {
                        this.animatingMask = true;
                        // Hide case card text for grid view
                        this.elm.classList.remove('m-home-featured-cases--grid');
                    },
                    onComplete: () => {
                        this.animatingMask = false;
                        this.maskState = size;
                        this.currentMaskPath = isDesktop ? maskDesktop : maskMobile;
                        window.dispatchEvent(new CustomEvent('enable-smooth-scroll'));
                        if(this.maskState==='grid') {
                            // Show case card text for grid view
                            this.elm.classList.add('m-home-featured-cases--grid');
                        }
                    }
                });

            }

            this.trackProgress = () => {

                if(!this.tl.scrollTrigger.progress) {
                    this.changeMask('initial');
                    return;
                } else {
                    if(this.tl.scrollTrigger.progress>=1) {
                        this.changeMask('grid');
                    } else {
                        this.changeMask('full');
                    }
                }

                const stepSize = 1 / this.slideCount;
                this.index = parseInt(this.tl.scrollTrigger.progress / stepSize);
                if(this.index>=this.slideCount) {
                    return;
                }
                if(this.index===this.activeSlideIndex) {
                    return;
                }

                this.changeSlide(this.index);
            }

            this.tl = gsap.timeline({
                scrollTrigger: {
                    trigger: this.elm,
                    start: "top top",
                    end: hasHover ? '+='+(window.innerHeight*0.3)*this.cases.length : '+='+(window.innerHeight*1.5)*this.cases.length,
                    pin: this.elm,
                    pinSpacing: true,
                    scrub: true,
                },
            });

            gsap.ticker.add(this.trackProgress);

        });


        // Build Timeline
        this.cases.forEach((elm) => {
            new SplitText(elm.querySelector('[data-case-client]'), { type: "lines" });
            new SplitText(elm.querySelector('[data-case-title]'), { type: "lines" });

            this.tl.fromTo(this.tlEl, {
                scaleX: 0
            }, {
                scaleX: 1,
                ease: 'none',
                duration: 1,
            });
        });

	}

    changeSlide(index) {
        if(index<this.activeSlideIndex) {
            this.hideSlide(this.activeSlideIndex);
        } else {
            if(index!=0) { // Never show first Slide (It's always there)
                this.showSlide(index);
            }
        }

        this.cases.forEach(elm => {
            this.hideSlideText(elm);
        });
        this.activeSlide = this.cases[index];
        this.showSlideText(this.activeSlide);
        this.activeSlideIndex = index;
        this.pagination.setAttribute('data-current-slide', index+1);

        window.dispatchEvent(new CustomEvent('disable-smooth-scroll'));

        let mm = gsap.matchMedia();

        mm.add({
            isMobile: `(max-width: 575px)`,
            isDesktop: `(min-width: 576px)`,
            reduceMotion: "(prefers-reduced-motion: reduce)",
            hasHover: "(hover: hover) and (pointer: fine)"
          }, (context) => {

            let { isDesktop, isMobile, reduceMotion, hasHover } = context.conditions;

            // Handle Video Play After Slide Change

            let video = null;
            if(isDesktop) {
                video = this.activeSlide.querySelector('.c-video-wrapper--landscape').querySelector('video');
            }
            if(isMobile) {
                video = this.activeSlide.querySelector('.c-video-wrapper--portrait').querySelector('video');
            }
            if(video) {
                video.dispatchEvent(new CustomEvent('play-video'));
            }

        });

        // Handle Video Pause After Slide Change
        this.cases.forEach((elm, slideIndex) => {
            if(this.activeSlideIndex==null||slideIndex===this.activeSlideIndex) {
                return;
            } else {
                const videos = elm.querySelectorAll('video');
                if(videos) {
                    videos.forEach(video => {
                        video.dispatchEvent(new CustomEvent('pause-video'));
                    });
                }
            }
        });
    }

    showSlide(index) {
        const slide = this.cases[index];
        gsap.to(slide.querySelector('[data-case-transition-media]'), {
            translateY: '0%',
            ease: 'power2.inOut',
            duration: 1,
            onComplete: () => {
                window.dispatchEvent(new CustomEvent('enable-smooth-scroll'));
            }
        });
    }

    hideSlide(index) {
        const slide = this.cases[index];
        if(!slide) {
            return;
        }
        gsap.to(slide.querySelector('[data-case-transition-media]'), {
            translateY: '100%',
            ease: 'power2.inOut',
            duration: 1,
            onComplete: () => {
                window.dispatchEvent(new CustomEvent('enable-smooth-scroll'));
            }
        });
    }

    showSlideText(slide) {
        if(!slide) {
            return;
        }
        slide.classList.add('c-home-featured-case--visible');
    }

    hideSlideText(slide) {
        if(!slide) {
            return;
        }
        slide.classList.remove('c-home-featured-case--visible');
    }

    destroy() {
        gsap.ticker.remove(this.trackProgress);
        window.removeEventListener('resize', this.boundResizeListener);
    }

}
